import type { WealthicaAccount, WealthicaConnection } from '../types';
import { persistAccountsAndPositionsImpl } from './persistence/persist';
import { deleteAccountDataImpl } from './persistence/delete';
import { setPersistDataPreferenceImpl } from './persistence/toggle';

type ProcessingState = typeof ConnectionProcessingState[keyof typeof ConnectionProcessingState] | string;

interface StoreState {
  isPersisting?: boolean;
  processingState?: ProcessingState;
  persistenceError?: string | null;
  persistDataPreference?: boolean;
}

type SetFn = (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>)) => void;
type GetFn = () => StoreState;

// Self-contained processing state enum
const ConnectionProcessingState = {
  IDLE: 'idle',
  CONNECTING: 'connecting', 
  LOADING_ACCOUNTS: 'loading_accounts',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  FAILED: 'failed',
  ERROR: 'error'
} as const;

/**
 * Persistence-related store actions - Clean self-contained module
 * Provides interface for account data persistence operations
 */
export const createPersistenceActions = (set: unknown, get: unknown) => {
  const _set = set as SetFn;
  const _get = get as GetFn;
  return ({
  // === PERSISTENCE ACTIONS ===
  persistAccountsAndPositions: async (
    userId: string,
    connectionData: WealthicaConnection,
    accounts: WealthicaAccount[],
    forceLocalOrOptions?: boolean | { isRefresh?: boolean; forceLocal?: boolean; toastId?: string | number; suppressSuccessToast?: boolean; mode?: 'both' | 'accounts_only' | 'positions_only' }
  ) => {
    await persistAccountsAndPositionsImpl(set, get, userId, connectionData, accounts, forceLocalOrOptions);
  },

  deleteAccountData: async (
    userId: string, 
    connectionData: WealthicaConnection, 
    preserveConnectionOrOptions: boolean | { preserveConnection?: boolean; deleteMode?: 'stop_syncing' | 'full_delete'; suppressSuccessToast?: boolean } = false
  ) => {
    await deleteAccountDataImpl(set, get, userId, connectionData, preserveConnectionOrOptions);
  },

  setPersistDataPreference: async (value: boolean) => {
    await setPersistDataPreferenceImpl(set, get, value);
  },

  setPersistenceError: (error: string | null) => {
    _set({ persistenceError: error });
  },

  resetState: () => {
    _set({
      isPersisting: false,
      processingState: ConnectionProcessingState.IDLE,
      persistenceError: null,
      persistDataPreference: true
    });
  }
});
}
